import React, { Suspense } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

// HOC.
import boost from 'app/client/utilities/hoc/boost';

// Routes.
import User from 'app/client/modules/User/lazy';
import Order from 'app/client/modules/Order/lazy';
import Customer from 'app/client/modules/Customer/lazy';
import IpAllowlist from 'app/client/modules/IpAllowList/lazy';
import CustomerPreferences from 'app/client/modules/CustomerPreferences/lazy';
import Tickets from 'app/client/modules/Tickets/lazy';
import Invoices from 'app/client/modules/Invoices/lazy';
import Products from 'app/client/modules/Products/lazy';
import Domains from 'app/client/modules/Domains/lazy';
import Certificates from 'app/client/modules/Certificates/lazy';
import Dns from 'app/client/modules/Dns/lazy';
import Servers from 'app/client/modules/Servers/lazy';
import Apps from 'app/client/modules/Apps/lazy';
import Secrets from 'app/client/modules/Secrets/lazy';
import Documents from 'app/client/modules/Documents/lazy';
import SecureMessage from 'app/client/modules/SecureMessage/lazy';
import Quotes from 'app/client/modules/Quotes/lazy';
import Pages from 'app/client/modules/Pages/lazy';
import Settings from 'app/client/modules/Settings/lazy';
import SupportTokens from 'app/client/modules/SupportTokens/lazy';
import Dashboard from 'app/client/modules/Dashboard/lazy';
import Surveys from 'app/client/modules/Surveys/lazy';
import DomainTransferToken from 'app/client/modules/DomainTransferToken/lazy';
import TicketFeedback from 'app/client/modules/TicketFeedback/lazy';
import Payment from 'app/client/modules/Invoices/Payment/lazy';
import ReleaseNotes from 'app/client/modules/ReleaseNotes/lazy';

// Components.
import NotFound from '../Pages/NotFound/lazy';

// HOC data.
import socket from './socket';
import store from './store';
import locales from './i18n';

// Hooks.
import useLanguageHandler from './hooks/useLanguageHandler';
import useThemeHandler from './hooks/useThemeHandler';
import useWaThemeClass from './hooks/useWaThemeClass';
import usePrivateOrderCleanup from './hooks/usePrivateOrderCleanup';
import useSpentRedirect from './hooks/useSpentRedirect';

// Local components.
import Layout from './components/Layout';

// Global styles.
import GlobalStyle, { RouteTransition } from './styled';

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const Core = ({
  authenticated,
  unlocked,
  language,
  customer,
  redirectTo = null,
  i18n,
  theme,
  autoSwitchTheme,
  setTheme,
  clearRedirect,
}) => {
  // When the language changes, set it.
  useLanguageHandler(i18n, language);

  useThemeHandler(autoSwitchTheme, theme, setTheme);
  useWaThemeClass(theme);

  // Remount the transition wrapper per top-level route segment, so switching
  // modules eases the new page in (navigation within a module does not).
  const { pathname } = useLocation();
  const segment = pathname.split('/')[1] || 'home';

  usePrivateOrderCleanup(authenticated);
  useSpentRedirect(redirectTo, clearRedirect);

  return (
    <>
      <GlobalStyle theme={theme} />
      <Layout
        authorized={authenticated && unlocked && typeof customer?.id !== 'undefined'}
        customer={customer}
      >
        <Suspense fallback={null}>
          <RouteTransition key={segment}>
            <SentryRoutes>
              <Route path="secure_message/*" element={<SecureMessage />} />
              <Route path="quotes/*" element={<Quotes />} />
              <Route path="surveys/*" element={<Surveys />} />
              <Route path="domains/transfer_token/*" element={<DomainTransferToken />} />
              <Route path="f/*" element={<TicketFeedback />} />
              <Route
                path="user/*"
                element={
                  <User
                    authenticated={authenticated}
                    unlocked={unlocked}
                    customer={customer?.id}
                    redirectTo={redirectTo}
                  />
                }
              />
              {(!authenticated || !unlocked) && (
                <Route path="*" element={<Navigate to="/user" replace />} />
              )}
              {authenticated && unlocked && customer && (
                <>
                  <Route index element={<Dashboard />} />
                  <Route path="customer/*" element={<Customer />} />
                  <Route path="order/*" element={<Order />} />
                  <Route path="tickets/*" element={<Tickets />} />
                  <Route path="products/*" element={<Products />} />
                  <Route path="invoices/*" element={<Invoices />} />
                  <Route path="domains/*" element={<Domains />} />
                  <Route path="certificates/*" element={<Certificates />} />
                  <Route path="dns/*" element={<Dns />} />
                  <Route path="servers/*" element={<Servers />} />
                  <Route path="apps/*" element={<Apps />} />
                  <Route path="secrets/*" element={<Secrets />} />
                  <Route path="documents/*" element={<Documents />} />
                  <Route path="ip_allowlist/*" element={<IpAllowlist />} />
                  <Route path="customer_preferences/*" element={<CustomerPreferences />} />
                  <Route path="secure_message/*" element={<SecureMessage />} />
                  <Route path="support_tokens" element={<SupportTokens />} />
                  <Route path="settings/*" element={<Settings />} />
                  <Route path="release-notes/*" element={<ReleaseNotes />} />
                  <Route path="*" element={<NotFound />} />
                </>
              )}
              <Route path="pages/*" element={<Pages />} />
              <Route path="invoices/:invoiceId/payment" element={<Payment />} />
              <Route
                path="*"
                element={
                  <Navigate to={!authenticated || !unlocked || !customer ? '/user' : '/'} replace />
                }
              />
            </SentryRoutes>
          </RouteTransition>
        </Suspense>
      </Layout>
    </>
  );
};

Core.propTypes = {
  authenticated: PropTypes.bool.isRequired,
  unlocked: PropTypes.bool.isRequired,
  language: PropTypes.string.isRequired,
  customer: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]).isRequired,
  redirectTo: PropTypes.string,
  i18n: PropTypes.shape({
    changeLanguage: PropTypes.func.isRequired,
  }).isRequired,
  theme: PropTypes.string.isRequired,
  autoSwitchTheme: PropTypes.bool.isRequired,
  setTheme: PropTypes.func.isRequired,
  clearRedirect: PropTypes.func.isRequired,
};

export default boost({ socket, store, i18n: locales })(Core);
